import { userService } from "src/services";
import {
  AppUserObject,
  DashboardObject,
  CreateHpsObj,
  TrendingSectorObj,
  UserObject,
  AppNotifyObj,
  FeatureFlagObj,
  CreateFnOObj,
  SubscriptionProfileData,
  PaymentMethod,
  UpdateSubscription,
  SubscriptionSuccessData,
  RazorpayVerifyPaymentData,
  GeneralSettingObj,
  AddSubscriptionObj,
  Watchlist,
  RazorpayOrderData,
  FcmTokenObj,
  MarkFeatureSeenObj,
  UpdateUniverseStocksObj,
} from "./table-objects";

import { User } from "firebase/auth";

import { encryptData, decryptData } from "./crypto";

interface RequestOptions {
  method: string;
  headers: {
    Authorization?: string;
    "Content-Type"?: string;
    "X-Encrypted"?: string;
    UserId?: string;
  };
  body?: string;
  signal?: AbortSignal;
}

type FetchOptions = {
  skipAuthLogout?: boolean;
};

const request = (method: string) => {
  return async (
    url: string,
    body?:
      | User
      | UserObject
      | DashboardObject
      | AppUserObject
      | AppNotifyObj
      | CreateHpsObj
      | CreateFnOObj
      | FeatureFlagObj[]
      | TrendingSectorObj[]
      | PaymentMethod
      | SubscriptionSuccessData
      | RazorpayVerifyPaymentData
      | RazorpayOrderData
      | UpdateSubscription
      | SubscriptionProfileData
      | GeneralSettingObj[]
      | AddSubscriptionObj[]
      | Watchlist
      | FcmTokenObj
      | MarkFeatureSeenObj
      | UpdateUniverseStocksObj
      | Record<string, unknown>,

    customHeaders: Record<string, string> = {},
    signal?: AbortSignal,
    fetchOptions: FetchOptions = {},
  ) => {
    const webHost = process.env.NEXT_PUBLIC_WEB_HOST || "http://localhost:3000";
    const applyEncryption = process.env.NEXT_PUBLIC_CRYPTO_APPLY === "true";
    const requestOptions: RequestOptions = {
      method,
      headers: { ...authHeader(), ...customHeaders },
      signal,
    };
    if (applyEncryption) requestOptions.headers["X-Encrypted"] = "true";

    let finalUrl = url;
    if (applyEncryption) {
      if (url.includes("?")) {
        const [path, queryString] = url.split("?");
        const params = Object.fromEntries(new URLSearchParams(queryString));
        const encryptedParams = await encryptData(params);
        finalUrl = `${path}?params=${encodeURIComponent(encryptedParams)}`;
      }

      if (body) {
        requestOptions.headers["Content-Type"] = "application/json";
        const encryptedBody = await encryptData(body);
        requestOptions.body = JSON.stringify({ data: encryptedBody });
      }
    } else {
      if (body) {
        requestOptions.headers["Content-Type"] = "application/json";
        requestOptions.body = JSON.stringify(body);
      }
    }
    const response = await fetch(webHost + finalUrl, requestOptions);
    return handleResponse(response, fetchOptions);
  };
};

const authHeader = () => {
  // return auth header with jwt if user is logged in and request is to the api url
  const user = userService.userValue;
  const isLoggedIn = user?.token;
  if (isLoggedIn) {
    return {
      Authorization: `Bearer ${user.token}`,
      UserId: user.uid,
    };
  } else {
    return {};
  }
};

const handleResponse = async (
  response: Response,
  fetchOptions: FetchOptions = {},
) => {
  const isJson = response.headers
    ?.get("content-type")
    ?.includes("application/json");

  let data = null;
  if (isJson) {
    const rawData = await response.json();
    if (response.headers.get("X-Encrypted") === "true" && rawData.data) {
      data = await decryptData(rawData.data);
    } else {
      data = rawData;
    }
  }

  // check for error response
  if (!response.ok) {
    if (
      [401].includes(response.status) &&
      userService.userValue &&
      !fetchOptions.skipAuthLogout &&
      !userService.loggingOut
    ) {
      userService.logout();
    }

    // get error message from body or default to response status
    const errorMsg =
      data?.error?.message || data?.message || response.statusText;

    if (data?.is_deleted) {
      return Promise.reject({ ...data, message: errorMsg });
    }

    return Promise.reject(errorMsg);
  }
  return data;
};

export const fetchWrapper = {
  get: request("GET"),
  post: request("POST"),
  put: request("PUT"),
  patch: request("PATCH"),
  delete: request("DELETE"),
};
